/**
 * Hero Mask Morph — genuinely morph the SVG clip mask (and its border outline)
 * between the desktop pill and mobile blob when the viewport crosses 1024px,
 * instead of hard-cutting between two clip references.
 *
 * Two `clip-path: url(#…)` references can't interpolate, so the two resting
 * shapes stay on their existing static clipPaths/borders (zero flash, no
 * regression — see the media queries + [data-hero-shape] rules in
 * _hero-home.scss). Only the *transition* runs on a third, animatable path:
 *
 *   1. Seed the morph path with the current shape, then hand the video's clip and
 *      the border over to the morph layer (`is-morphing`) — seamless because the
 *      morph path equals the resting shape at t=0.
 *   2. Tween the shared points array desktop⇄mobile, rewriting the clip `d`, the
 *      border `d` + stroke width, and the box aspect-ratio/size each frame. Timing
 *      and easing come from the motion tokens via `tween()`.
 *   3. On finish, flip [data-hero-shape] so the static CSS takes back over (again
 *      seamless: the morph path equals the resting shape at t=1) and drop the
 *      inline overrides.
 *
 * Reduced-motion visitors get an instant swap with no morph.
 */

import { tween } from './motion-choreography.js';
import { SHAPES, buildPath, lerpPoints } from './hero-shapes.js';

// Mirrors the SCSS breakpoint and hero-video.js: 1024px+ is the desktop layout.
const DESKTOP = '(min-width: 1024px)';
// Tier 2 expressive-entrance token (long + emphasized) — a branded reshape.
const MOTION = 'long emphasized';

const shapeFor = (mql) => (mql.matches ? 'desktop' : 'mobile');
const prefersReduced = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const rootFontPx = () =>
  parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
const toPx = (v) =>
  String(v).endsWith('rem') ? parseFloat(v) * rootFontPx() : parseFloat(v);
const lerp = (a, b, t) => a + (b - a) * t;

function initHeroMask() {
  const hero = document.querySelector('.c-hero-home');
  if (!hero) return;
  const media = hero.querySelector('.c-hero-home__media');
  const clipPath = hero.querySelector('#hero-home-clip-morph path');
  const borderPath = hero.querySelector('.c-hero-home__border--morph path');
  if (!media || !clipPath || !borderPath) return;
  const video = hero.querySelector('.c-hero-home__video');

  const mql = window.matchMedia(DESKTOP);
  // Seed the resting state so the static CSS matches the viewport with no flash.
  hero.dataset.heroShape = shapeFor(mql);

  let running = false;

  const paint = (from, to, t) => {
    const d = buildPath(lerpPoints(from.points, to.points, t));
    clipPath.setAttribute('d', d);
    borderPath.setAttribute('d', d);
    borderPath.setAttribute('stroke-width', lerp(from.stroke, to.stroke, t));
    media.style.aspectRatio = String(lerp(from.aspectRatio, to.aspectRatio, t));
    media.style.maxWidth = `${lerp(toPx(from.maxWidth), toPx(to.maxWidth), t)}px`;
  };

  const onChange = () => {
    if (running) return;
    const next = shapeFor(mql);
    if (hero.dataset.heroShape === next) return;

    if (prefersReduced()) {
      hero.dataset.heroShape = next;
      return;
    }

    const from = SHAPES[hero.dataset.heroShape];
    const to = SHAPES[next];
    running = true;

    // Seed the morph path with the current shape *before* the video clips to it,
    // so there's no unclipped frame at the handoff.
    paint(from, to, 0);
    hero.classList.add('is-morphing');
    if (video) video.style.clipPath = 'url(#hero-home-clip-morph)';

    tween(
      MOTION,
      (t) => paint(from, to, t),
      () => {
        // Hand back to the static CSS (matches the morph's t=1 shape), then drop
        // the transient overrides.
        hero.dataset.heroShape = next;
        hero.classList.remove('is-morphing');
        if (video) video.style.clipPath = '';
        media.style.aspectRatio = '';
        media.style.maxWidth = '';
        running = false;

        // A crossing landed mid-morph — settle to the current viewport.
        if (shapeFor(mql) !== next) onChange();
      }
    );
  };

  mql.addEventListener('change', onChange);
}

document.addEventListener('DOMContentLoaded', initHeroMask);
